import { useTranslation } from "react-i18next";
import { Hero } from "@/components/Hero";
import { JourneyHero } from "@/components/journey/JourneyHero";
import { Marquee } from "@/components/Marquee";
import { JourneyStages, ServicesGrid, Testimonials, FinalCTA } from "@/components/LandingSections";
import { SEO } from "@/components/SEO";
import { useSiteContent } from "@/context/SiteContentContext";

function TrustStrip() {
  const { t } = useTranslation();
  const rows = [
    { key: "vision", label: t("trustStrip.vision"), value: t("trustStrip.visionValue") },
    { key: "companies", label: t("trustStrip.companies"), value: t("trustStrip.companiesValue") },
    { key: "experts", label: t("trustStrip.experts"), value: t("trustStrip.expertsValue") },
    { key: "sla", label: t("trustStrip.sla"), value: t("trustStrip.slaValue") },
  ];
  return (
    <section className="border-y border-border/60 bg-muted/20" data-testid="trust-strip">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-5 sm:py-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
        {rows.map((r) => (
          <div key={r.key} className="text-center" data-testid={`trust-item-${r.key}`}>
            <p className="font-display text-xl sm:text-2xl font-semibold tabular-nums">{r.value}</p>
            <p className="text-[10px] sm:text-[11px] uppercase tracking-widest text-muted-foreground mt-0.5">{r.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

// Registry of admin-configurable landing blocks. Keys must match the ids
// used in content.landing_layout.order / .visibility.
const BLOCK_COMPONENTS = {
  hero: Hero,
  journey_hero: JourneyHero,
  marquee: Marquee,
  trust_strip: TrustStrip,
  stages: JourneyStages,
  services: ServicesGrid,
  testimonials: Testimonials,
  final_cta: FinalCTA,
};
const DEFAULT_ORDER = ["journey_hero", "hero", "marquee", "trust_strip", "stages", "services", "testimonials", "final_cta"];

export default function Landing() {
  const { content } = useSiteContent();
  const layout = content?.landing_layout || {};
  const order = Array.isArray(layout.order) && layout.order.length ? layout.order : DEFAULT_ORDER;
  const visibility = layout.visibility || {};

  const seen = new Set();
  const finalOrder = [];
  for (const id of order) {
    if (BLOCK_COMPONENTS[id] && !seen.has(id)) {
      seen.add(id);
      finalOrder.push(id);
    }
  }
  for (const id of DEFAULT_ORDER) {
    if (!seen.has(id)) finalOrder.push(id);
  }

  // Header is light. First visible block after the header renders dark, then
  // sections alternate: dark → light → dark → …
  let alternationIdx = 0;

  return (
    <div data-testid="landing-page">
      <SEO
        title="Enter, operate, and scale in Saudi Arabia"
        description="One business operating system for market entry, shared services, and advisory in the Saudi Arabian and MEA region."
        path="/"
        keywords="Saudi Arabia market entry, KSA business setup, Saudi Vision 2030, business operating system, shared services KSA, Riyadh company formation, MEA advisory, Saudi Arabia expansion"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "The Saudi Gate",
          url: "https://startupgate.sa",
          logo: "https://startupgate.sa/logo_transparent.png",
          description: "Business operating system for market entry, shared services, and advisory in Saudi Arabia and the MEA region.",
          address: { "@type": "PostalAddress", addressLocality: "Riyadh", addressCountry: "SA" },
        }}
      />
      {finalOrder.map((id) => {
        if (visibility[id] === false) return null;
        const Comp = BLOCK_COMPONENTS[id];
        const isDark = alternationIdx % 2 === 0; // start with dark right after the light header
        alternationIdx += 1;
        return (
          <div key={id} className={isDark ? "dark bg-background text-foreground" : "bg-background text-foreground"}>
            <Comp />
          </div>
        );
      })}
    </div>
  );
}
